/**
 * The authentication gate that sits above the whole application.
 *
 * ORDER MATTERS HERE. `AppProvider` fetches `/api/bootstrap` on mount, and that route is guarded,
 * so it must not mount until we know a session exists. Mounting it unconditionally would 401 on
 * every visit to the login page and trip the session guard into redirecting to the page the user
 * is already on.
 *
 * So: check the session first, render exactly one of three things, and never both.
 */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import App from './App'
import { Spinner } from './components/ui/primitives'
import { AppProvider } from './contexts/app_context'
import { useAuth } from './contexts/auth_context'
import { LoginPage } from './pages/login'

function AuthenticatedApp() {
  const { checking, session } = useAuth()
  const location = useLocation()

  if (checking) {
    //  The first paint, before /auth/session answers. Rendering the login form here would flash
    //  it at every already-signed-in user on every page load.
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spinner />
      </div>
    )
  }

  if (!session.authenticated) {
    //  `state` carries where they were headed so the login can return them there later. `replace`
    //  keeps the unauthenticated URL out of history, so Back does not bounce between the two.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return (
    <AppProvider>
      <App />
    </AppProvider>
  )
}

export default function Root() {
  return (
    <Routes>
      {/* Public. Everything else falls through to the gate. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<AuthenticatedApp />} />
    </Routes>
  )
}
